/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import { type QueryClient, keepPreviousData, useQuery } from '@tanstack/react-query';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import type { CertonaItem } from '@/types';
import type { RecommendationsResponse } from '../interface';
import { transformRecommendations } from '../utils/transformRecommendations';
import { useRecommendationParams } from '../hooks/useRecommendationParams';
import { type GetRecommendationsParamsModel } from '@/api/types/product-recommendations-types';
import environmentConfig from '@/config/environment';

const RECOMMENDATIONS_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-recommendations/v1/recommendations`;
const STALE_TIME_5_MINUTES = 5 * 60000;

/**
 * Fetches recommendations from the new recommendation API
 */
const getRecommendations = async (
  params: GetRecommendationsParamsModel,
  axiosInstance?: AxiosInstance,
): Promise<{ items: CertonaItem[] }> => {
  const response = await getAxios(axiosInstance).get<RecommendationsResponse>(RECOMMENDATIONS_URL, {
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    params: {
      ...params,
      strictFiltering: environmentConfig.TARGET_ENV === 'production',
    },
  });

  // Merge items from all serving configs
  const allItems: CertonaItem[] = [];

  for (const [, servingConfigData] of Object.entries(response.data)) {
    if (servingConfigData?.items) {
      const transformedItems = transformRecommendations(
        servingConfigData.items,
        servingConfigData.attributionToken,
      );
      allItems.push(...transformedItems);
    }
  }

  return { items: allItems };
};

/**
 * Query key factory for recommendations
 */
export const recommendationsQueryKey = (params: GetRecommendationsParamsModel) =>
  ['recommendations', params] as const;

/**
 * Hook to fetch product recommendations
 * Conditionally enabled based on personalization_engine_v2 Monetate flag
 */
export const useRecommendations = ({
  servingConfig,
  productId,
  recordsPerPage,
  validateOnly,
  enabled = true,
}: {
  servingConfig: string;
  productId: string;
  recordsPerPage?: number;
  validateOnly?: boolean;
  enabled?: boolean;
}) => {
  const params = useRecommendationParams({
    servingConfig,
    productId,
    recordsPerPage,
    validateOnly,
  });

  const personalizationFlag = useMonetateDecisionFlag('personalization_engine_v2');

  return useQuery({
    queryKey: recommendationsQueryKey(params),
    queryFn: () => getRecommendations(params),
    enabled: enabled && !!personalizationFlag && !!params.visitorId && !!productId,
    staleTime: STALE_TIME_5_MINUTES,
    placeholderData: keepPreviousData,
  });
};

/**
 * Prefetch recommendations (for SSR)
 */
export const prefetchRecommendations = async (
  queryClient: QueryClient,
  params: GetRecommendationsParamsModel,
  axiosInstance: AxiosInstance,
) => {
  return queryClient.prefetchQuery({
    queryKey: recommendationsQueryKey(params),
    queryFn: () => getRecommendations(params, axiosInstance),
    staleTime: STALE_TIME_5_MINUTES,
  });
};

/**
 * Get recommendations from cache
 */
export const getRecommendationsFromCache = (
  queryClient: QueryClient,
  params: GetRecommendationsParamsModel,
) => {
  return queryClient.getQueryData(recommendationsQueryKey(params));
};
